import { useMemo, useState } from "react";
import PageShell from "@/components/feature/PageShell";
import CategoryPage from "@/components/feature/CategoryPage";
import FilterGroup from "@/components/base/FilterGroup";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import NewsCard from "@/components/feature/NewsCard";
import { useSeo } from "@/hooks/useSeo";
import { newsList, type NewsTag } from "@/mocks/news";

const PAGE_SIZE = 6;

const tagOptions = [
  { label: "全部资讯", value: "全部" },
  { label: "行业动态", value: "行业动态" },
  { label: "动画介绍", value: "动画介绍" },
  { label: "新闻速递", value: "新闻速递" },
];

const sortOptions = [
  { label: "最新发布", value: "latest" },
  { label: "最多阅读", value: "hot" },
];

export default function NewsPage() {
  useSeo(
    "age动漫 - 动漫资讯分类页",
    "age动漫资讯分类页，汇集最新动漫行业动态、新番动画介绍与新闻速递，按时间与热度排序，快速了解二次元行业正在发生什么。",
    "age动漫资讯,动漫新闻,行业动态,动画介绍,新番情报",
  );

  const [tag, setTag] = useState("全部");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = newsList.filter((item) => tag === "全部" || item.tag === (tag as NewsTag));
    return [...list].sort((a, b) =>
      sort === "hot" ? b.views - a.views : b.publishedAt.localeCompare(a.publishedAt),
    );
  }, [tag, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const current = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const reset = () => {
    setTag("全部");
    setSort("latest");
    setPage(1);
  };

  const changePage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 240, behavior: "smooth" });
  };

  return (
    <PageShell>
      <CategoryPage
        breadcrumb={[{ label: "首页", to: "/" }, { label: "资讯" }]}
        kicker="NEWS"
        title="动漫资讯"
        description="覆盖行业动态、动画介绍与新闻速递三类内容。从制作阵容到产业数据，我们只整理有信息量的部分，帮你在有限的时间里跟上二次元的节奏。"
        meta={
          <div className="flex items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-newspaper-line text-primary-500"></i>
              收录 {newsList.length} 篇
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-primary-500"></i>
              每日更新
            </span>
          </div>
        }
        filterContent={
          <>
            <FilterGroup
              title="内容标签"
              options={tagOptions}
              value={tag}
              onChange={(value) => {
                setTag(value);
                setPage(1);
              }}
            />
            <FilterGroup
              title="排序方式"
              options={sortOptions}
              value={sort}
              onChange={(value) => {
                setSort(value);
                setPage(1);
              }}
              grid
            />
            <div className="rounded-md bg-background-100 p-3">
              <p className="text-[11px] text-foreground-500 leading-relaxed">
                筛选条件变化后列表会立即刷新，分页会自动回到第一页。
              </p>
            </div>
          </>
        }
        count={filtered.length}
        onReset={reset}
      >
        {current.length === 0 ? (
          <EmptyState
            title="未找到相关资讯"
            description="当前筛选条件下暂无内容，可以尝试切换标签或重置筛选条件。"
            action={
              <button
                type="button"
                onClick={reset}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            }
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {current.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
            <div className="mt-8">
              <Pagination currentPage={safePage} totalPages={totalPages} onChange={changePage} />
            </div>
          </>
        )}
      </CategoryPage>
    </PageShell>
  );
}